import React, { Component } from "react";
import axios from "axios";
import { connect } from "react-redux";
import Select from "react-select";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Modal, ModalHeader, ModalBody, ModalFooter } from "reactstrap";
import { ADR_EDIT } from "../../_constants/actionConstants";
import { BASE_URL } from "../../_constants/urlConstants";
import equipmentActions from "../../_actions/equipmentActions";
import EquipmentCatalogBrowserModal from "../_common/EquipmentCatalogBrowserModal";

const MODE = { MANUAL: "manual", STANDARD: "standard", MISC: "misc" };

const MODE_SLOT_KEY = {
  [MODE.STANDARD]: "standardSelection",
  [MODE.MISC]: "miscSelection",
  [MODE.MANUAL]: "manualEntry",
};

const BLANK_CATALOG_SELECTION = {
  equipmentClass: null, equipmentClassDescription: "", selectedClassOption: null,
  equipmentMake: null, equipmentMakeDescription: "", selectedMakeOption: null,
  equipmentModel: null, equipmentModelDescription: "", selectedModelOption: null,
};

const norm = (s) => (s || "").trim().toUpperCase();

// "" -> null, "0" -> 0 (unlike `parseFloat(v) || null`, which would wrongly
// turn a legitimate zero rate into null since 0 is falsy).
const parseRate = (v) => {
  const trimmed = (v || "").toString().trim();
  return trimmed === "" ? null : parseFloat(trimmed);
};

/**
 * Builds catalog year options (April 1 – March 31).
 * Returns the 6 most recent years ending with the FY that contains referenceDate.
 * @param {Date|string|null} referenceDate
 * @returns {{ value: string, label: string }[]}
 */
function buildCatalogYearOptions(referenceDate) {
  const d = referenceDate ? new Date(referenceDate) : new Date();
  // April starts FY: month index 3 = April
  const fyStartYear = d.getMonth() >= 3 ? d.getFullYear() : d.getFullYear() - 1;
  return Array.from({ length: 6 }, (_, i) => {
    const y = fyStartYear - (5 - i);
    return {
      value: `${y}-04-01`,
      label: `${String(y).slice(2)}/${String(y + 1).slice(2)}`,
    };
  });
}

class AddEquipmentModalV3 extends Component {
  constructor(props) {
    super(props);
    const catalogYearOptions = buildCatalogYearOptions(props.reportDate);
    this.state = {
      mode: MODE.STANDARD,
      selectedCatalogYear: catalogYearOptions[catalogYearOptions.length - 1],
      catalogYearOptions,
      formData: {
        contractorId: null,
        selectedContractor: null,
        equipmentNumber: "",
        description: "",
        isRental: false,
      },
      // Each entry mode keeps its own independent Class/Make/Code (+ Rate for
      // Manual) so switching modes never discards what was entered elsewhere —
      // it's a pure display swap, not a shared-then-reset field.
      standardSelection: { ...BLANK_CATALOG_SELECTION },
      miscSelection: { ...BLANK_CATALOG_SELECTION },
      manualEntry: {
        equipmentClass: "", equipmentMake: "", equipmentModel: "",
        rate: "", delayFactor: "", otFactor: "",
      },
      catalog: {
        classes: [],
        makes: [],
        models: [],
        loadingClasses: false,
        loadingMakes: false,
        loadingModels: false,
      },
      catalogRate: null,
      reconciling: false,
      search: { query: "", results: [], loading: false },
      showCatalogBrowser: false,
    };
    this._searchTimer = null;
  }

  componentDidMount() {
    const { contractorId, contractorOptions } = this.props;
    const selectedContractor = contractorId
      ? (contractorOptions || []).find((co) => co.value === contractorId)
      : null;
    if (contractorId)
      this.setState({ formData: { ...this.state.formData, contractorId, selectedContractor } });
    this.loadClasses("E");
  }

  // ── Helpers ──────────────────────────────────────────────────────────────

  get reportDateStr() {
    const { selectedCatalogYear } = this.state;
    return selectedCatalogYear ? selectedCatalogYear.value : new Date().toISOString().split("T")[0];
  }

  get equipmentTypeParam() {
    return this.state.mode === MODE.STANDARD ? "E" : "M";
  }

  // Standard/Misc/Manual each have their own Class/Make/Code slot; these resolve
  // "the slot for whichever mode is currently active" so every handler below can
  // stay mode-agnostic.
  getActiveSelection = () => this.state[MODE_SLOT_KEY[this.state.mode]];

  setActiveSelection = (partial, extraState, callback) => {
    const key = MODE_SLOT_KEY[this.state.mode];
    this.setState((s) => ({ [key]: { ...s[key], ...partial }, ...extraState }), callback);
  };

  getDuplicateNumbers = () => {
    const { formData } = this.state;
    const { projEquipList } = this.props;
    if (!formData.contractorId || !formData.equipmentNumber.trim()) return [];
    return formData.equipmentNumber.split(",")
      .map((n) => n.trim())
      .filter((n) => n && (projEquipList || []).some(
        (e) => e.contractorId === formData.contractorId &&
               (e.number || "").toLowerCase() === n.toLowerCase()
      ));
  };

  // ── Mode / year change ───────────────────────────────────────────────────
  //
  // Switching modes/years preserves every mode's Class/Make/Code/Rate — only the
  // catalog reference data (tied to type+year) is reset, then the newly-active
  // slot is reconciled against it (see reconcileCatalogSelection).

  handleModeChange = (mode) => {
    this.setState(
      {
        mode,
        search: { query: "", results: [], loading: false },
        catalog: { ...this.state.catalog, classes: [], makes: [], models: [] },
        catalogRate: null,
      },
      () => { if (mode !== MODE.MANUAL) this.reconcileCatalogSelection(); }
    );
  };

  handleCatalogYearChange = (selectedOption) => {
    this.setState(
      {
        selectedCatalogYear: selectedOption,
        search: { query: "", results: [], loading: false },
        catalog: { ...this.state.catalog, classes: [], makes: [], models: [] },
        catalogRate: null,
      },
      () => { if (this.state.mode !== MODE.MANUAL) this.reconcileCatalogSelection(); }
    );
  };

  // A <Select> can only ever show a valid catalog option or nothing, so whenever
  // the active mode's slot might now be stale (its own catalog year changed, or
  // it's being viewed for the first time), its stored Class/Make/Model is
  // checked against the current catalog and cleared — cascading down — the
  // moment it no longer matches. This never touches the *other* two slots.
  reconcileCatalogSelection = async () => {
    // Captured so a slower-to-resolve reconciliation from a mode/year the user has since
    // switched away from can detect it's stale and bail instead of writing its match into
    // whatever mode happens to be active by the time its awaited fetch comes back.
    const reconcileMode = this.state.mode;
    const stillCurrent = () => this.state.mode === reconcileMode;

    const slot = this.getActiveSelection(); // snapshot, same pattern as today
    const equipmentType = this.equipmentTypeParam;

    this.setState({ reconciling: true });

    const classes = await this.loadClasses(equipmentType);
    if (!stillCurrent()) return;
    const classMatch = slot.equipmentClass
      ? classes.find((c) => norm(c.equipmentClass) === norm(slot.equipmentClass))
      : null;

    if (!classMatch) {
      this.setActiveSelection({ ...BLANK_CATALOG_SELECTION },
        { reconciling: false, catalogRate: null, catalog: { ...this.state.catalog, makes: [], models: [] } });
      return;
    }

    const classOption = {
      value: classMatch.equipmentClass,
      label: `[ ${classMatch.equipmentClass} ] ${classMatch.equipmentClassDescription}`,
      description: classMatch.equipmentClassDescription,
    };
    this.setActiveSelection({
      selectedClassOption: classOption,
      equipmentClass: classMatch.equipmentClass,
      equipmentClassDescription: classOption.description,
    });

    const makes = await this.loadMakes(classMatch.equipmentClass);
    if (!stillCurrent()) return;
    const makeMatch = slot.equipmentMake
      ? makes.find((m) => norm(m.equipmentMake) === norm(slot.equipmentMake))
      : null;

    if (!makeMatch) {
      this.setActiveSelection(
        { selectedMakeOption: null, equipmentMake: null, equipmentMakeDescription: "", selectedModelOption: null, equipmentModel: null, equipmentModelDescription: "" },
        { reconciling: false, catalogRate: null, catalog: { ...this.state.catalog, models: [] } });
      return;
    }

    const makeOption = {
      value: makeMatch.equipmentMake,
      label: `[ ${makeMatch.equipmentMake} ] ${makeMatch.equipmentMakeDescription}`,
      description: makeMatch.equipmentMakeDescription,
    };
    this.setActiveSelection({
      selectedMakeOption: makeOption,
      equipmentMake: makeMatch.equipmentMake,
      equipmentMakeDescription: makeOption.description,
    });

    // loadModels resolves the rate itself when the make has no model codes at all.
    const models = await this.loadModels(classMatch.equipmentClass, makeMatch.equipmentMake);
    if (!stillCurrent()) return;

    if (models.length === 0) {
      this.setActiveSelection({ selectedModelOption: null, equipmentModel: null, equipmentModelDescription: "" }, { reconciling: false });
      return;
    }

    const modelMatch = slot.equipmentModel
      ? models.find((m) => norm(m.equipmentModel) === norm(slot.equipmentModel))
      : null;

    if (!modelMatch) {
      this.setActiveSelection({ selectedModelOption: null, equipmentModel: null, equipmentModelDescription: "" }, { reconciling: false, catalogRate: null });
      return;
    }

    const modelOption = {
      value: modelMatch.equipmentModel,
      label: `[ ${modelMatch.equipmentModel} ] ${modelMatch.equipmentModelDescription}`,
      description: modelMatch.equipmentModelDescription,
    };
    this.setActiveSelection(
      { selectedModelOption: modelOption, equipmentModel: modelMatch.equipmentModel, equipmentModelDescription: modelOption.description },
      { reconciling: false }
    );
    await this.loadRate(classMatch.equipmentClass, makeMatch.equipmentMake, modelMatch.equipmentModel);
  };

  // ── Catalog API calls (each returns the fetched list so callers can use it
  // synchronously without waiting on a second render) ─────────────────────────

  loadClasses = async (equipmentType) => {
    this.setState((s) => ({ catalog: { ...s.catalog, loadingClasses: true, classes: [] } }));
    try {
      const res = await axios.get(`${BASE_URL}/api/ProjectEquipment/Catalog/Classes`, {
        params: { equipmentType, reportDate: this.reportDateStr },
      });
      const classes = res.data || [];
      this.setState((s) => ({ catalog: { ...s.catalog, loadingClasses: false, classes } }));
      return classes;
    } catch {
      this.setState((s) => ({ catalog: { ...s.catalog, loadingClasses: false } }));
      return [];
    }
  };

  loadMakes = async (equipmentClass) => {
    const equipmentType = this.equipmentTypeParam;
    this.setState((s) => ({ catalog: { ...s.catalog, loadingMakes: true, makes: [], models: [] } }));
    try {
      const res = await axios.get(`${BASE_URL}/api/ProjectEquipment/Catalog/Makes`, {
        params: { equipmentClass, equipmentType, reportDate: this.reportDateStr },
      });
      const makes = res.data || [];
      this.setState((s) => ({ catalog: { ...s.catalog, loadingMakes: false, makes } }));
      return makes;
    } catch {
      this.setState((s) => ({ catalog: { ...s.catalog, loadingMakes: false } }));
      return [];
    }
  };

  loadModels = async (equipmentClass, equipmentMake) => {
    const equipmentType = this.equipmentTypeParam;
    this.setState((s) => ({ catalog: { ...s.catalog, loadingModels: true, models: [] } }));
    try {
      const res = await axios.get(`${BASE_URL}/api/ProjectEquipment/Catalog/Models`, {
        params: { equipmentClass, equipmentMake, equipmentType, reportDate: this.reportDateStr },
      });
      const models = res.data || [];
      this.setState((s) => ({ catalog: { ...s.catalog, loadingModels: false, models } }));
      // No model codes for this make — fetch rate directly at class+make level
      if (models.length === 0) {
        this.loadRate(equipmentClass, equipmentMake, null);
      }
      return models;
    } catch {
      this.setState((s) => ({ catalog: { ...s.catalog, loadingModels: false } }));
      return [];
    }
  };

  loadRate = async (equipmentClass, equipmentMake, equipmentModel) => {
    try {
      const res = await axios.get(`${BASE_URL}/api/ProjectEquipment/Catalog/Rate`, {
        params: {
          equipmentClass, equipmentMake, equipmentModel,
          equipmentType: this.equipmentTypeParam, reportDate: this.reportDateStr,
        },
      });
      this.setState({ catalogRate: res.data || null });
    } catch {
      this.setState({ catalogRate: null });
    }
  };

  // ── Catalog search ───────────────────────────────────────────────────────

  handleSearchChange = (e) => {
    const query = e.target.value;
    this.setState((s) => ({ search: { ...s.search, query, results: [] } }));
    clearTimeout(this._searchTimer);
    const hasValidSegment = query.split("/").some((seg) => seg.trim().length >= 2);
    if (hasValidSegment) {
      this._searchTimer = setTimeout(() => this.loadSearch(query), 300);
    }
  };

  loadSearch = async (query) => {
    const equipmentType = this.equipmentTypeParam;
    this.setState((s) => ({ search: { ...s.search, loading: true } }));
    try {
      const res = await axios.get(`${BASE_URL}/api/ProjectEquipment/Catalog/Search`, {
        params: { query, equipmentType, reportDate: this.reportDateStr },
      });
      this.setState((s) => ({ search: { ...s.search, loading: false, results: res.data || [] } }));
    } catch {
      this.setState((s) => ({ search: { ...s.search, loading: false } }));
    }
  };

  applyCatalogMatch = (result) => {
    const classOption = {
      value: result.equipmentClass,
      label: `[ ${result.equipmentClass} ] ${result.equipmentClassDescription}`,
    };
    const makeOption = {
      value: result.equipmentMake,
      label: `[ ${result.equipmentMake} ] ${result.equipmentMakeDescription}`,
    };
    const modelOption = {
      value: result.equipmentModel,
      label: `[ ${result.equipmentModel} ] ${result.equipmentModelDescription}`,
      description: result.equipmentModelDescription,
    };
    this.setActiveSelection(
      {
        equipmentClass: result.equipmentClass,
        equipmentClassDescription: result.equipmentClassDescription,
        selectedClassOption: classOption,
        equipmentMake: result.equipmentMake,
        equipmentMakeDescription: result.equipmentMakeDescription,
        selectedMakeOption: makeOption,
        equipmentModel: result.equipmentModel,
        equipmentModelDescription: result.equipmentModelDescription,
        selectedModelOption: modelOption,
      },
      {
        search: { query: "", results: [], loading: false },
        catalog: { ...this.state.catalog, makes: [], models: [] },
        catalogRate: { rate: result.rate, otFactor: result.otFactor, delayFactor: result.delayFactor },
      }
    );
  };

  handleSearchSelect = (result) => this.applyCatalogMatch(result);

  // The browser popup can be flipped to the *other* catalog type without closing it
  // (see EquipmentCatalogBrowserModal), so a selected row may not match whatever mode
  // is currently active here — switch to the mode matching the row's own type first,
  // loading that mode's Class list so its dropdown stays usable, before writing the match
  // into that mode's slot (never the still-active one).
  handleCatalogBrowserSelect = (result) => {
    const resultMode = result.equipmentType === "M" ? MODE.MISC : MODE.STANDARD;
    if (resultMode !== this.state.mode) {
      this.setState(
        {
          mode: resultMode,
          search: { query: "", results: [], loading: false },
          catalog: { ...this.state.catalog, classes: [], makes: [], models: [] },
          catalogRate: null,
        },
        () => {
          this.loadClasses(this.equipmentTypeParam);
          this.applyCatalogMatch(result);
        }
      );
    } else {
      this.applyCatalogMatch(result);
    }
    this.setState({ showCatalogBrowser: false });
  };

  // ── Field handlers ───────────────────────────────────────────────────────

  handleChange = (e) =>
    this.setState({
      formData: { ...this.state.formData, [e.target.name]: e.target.value },
    });

  handleChange_check = (e) =>
    this.setState({
      formData: { ...this.state.formData, [e.target.name]: e.target.checked },
    });

  handleChange_contractor = (selectedOption) =>
    this.setState({
      formData: {
        ...this.state.formData,
        contractorId: selectedOption.value,
        selectedContractor: selectedOption,
      },
    });

  // Manual mode's free-text Class/Make/Code and Rate/DelayFactor/OTFactor all
  // live in the active slot (manualEntry), not formData.
  handleChangeManualField = (e) =>
    this.setActiveSelection({ [e.target.name]: e.target.value });

  handleChange_class = (selectedOption) => {
    this.setActiveSelection(
      {
        equipmentClass: selectedOption ? selectedOption.value : null,
        equipmentClassDescription: selectedOption ? selectedOption.description : "",
        selectedClassOption: selectedOption,
        equipmentMake: null,
        equipmentModel: null,
        equipmentMakeDescription: "",
        equipmentModelDescription: "",
        selectedMakeOption: null,
        selectedModelOption: null,
      },
      {
        catalog: { ...this.state.catalog, makes: [], models: [] },
        formData: { ...this.state.formData, description: "" },
        catalogRate: null,
      },
      () => { if (selectedOption) this.loadMakes(selectedOption.value); }
    );
  };

  handleChange_make = (selectedOption) => {
    const active = this.getActiveSelection();
    this.setActiveSelection(
      {
        equipmentMake: selectedOption ? selectedOption.value : null,
        equipmentMakeDescription: selectedOption ? selectedOption.description : "",
        selectedMakeOption: selectedOption,
        equipmentModel: null,
        equipmentModelDescription: "",
        selectedModelOption: null,
      },
      {
        catalog: { ...this.state.catalog, models: [] },
        formData: { ...this.state.formData, description: "" },
        catalogRate: null,
      },
      () => {
        if (selectedOption && active.equipmentClass)
          this.loadModels(active.equipmentClass, selectedOption.value);
      }
    );
  };

  handleChange_model = (selectedOption) => {
    const active = this.getActiveSelection();
    this.setActiveSelection(
      {
        equipmentModel: selectedOption ? selectedOption.value : null,
        equipmentModelDescription: selectedOption ? selectedOption.description : "",
        selectedModelOption: selectedOption,
      },
      { catalogRate: null },
      () => { if (selectedOption) this.loadRate(active.equipmentClass, active.equipmentMake, selectedOption.value); }
    );
  };

  // ── Validation & submit ──────────────────────────────────────────────────

  formIsIncomplete = () => {
    const { formData, mode, catalog, reconciling } = this.state;
    const active = this.getActiveSelection();
    if (!formData.contractorId) return true;
    if (!formData.equipmentNumber.trim()) return true;
    if (mode === MODE.MANUAL) return !formData.description.trim();
    if (reconciling) return true;
    const busy = catalog.loadingClasses || catalog.loadingMakes || catalog.loadingModels;
    const noModelsAvailable = !busy && !!active.equipmentMake && catalog.models.length === 0 && !active.selectedModelOption;
    return (
      !active.equipmentClass ||
      !active.equipmentMake ||
      (!active.equipmentModel && !noModelsAvailable)
    );
  };

  handleSubmit = async (e) => {
    const { dispatch, projectId, tabId, rowKey, toggleAddEquipmentModal, markAdrDirty } = this.props;
    const { formData, mode } = this.state;
    const active = this.getActiveSelection();
    e.preventDefault();
    if (this.formIsIncomplete()) return;

    const equipmentNumbers = formData.equipmentNumber
      .split(",")
      .map((n) => n.trim())
      .filter(Boolean);

    for (const num of equipmentNumbers) {
      const newEquipment = await dispatch(
        equipmentActions.add({
          projectId,
          contractorId: formData.contractorId,
          equipmentNumber: num,
          description: formData.description.trim(),
          isRental: formData.isRental,
          isManualEntry: mode === MODE.MANUAL,
          ...(mode !== MODE.MANUAL
            ? {
                equipmentClass: active.equipmentClass,
                equipmentMake: active.equipmentMake,
                equipmentModel: active.equipmentModel,
                equipmentClassDescription: active.equipmentClassDescription,
                equipmentMakeDescription: active.equipmentMakeDescription,
                equipmentModelDescription: active.equipmentModelDescription,
                equipmentType: mode === MODE.STANDARD ? "E" : "M",
              }
            : {
                equipmentClass: (active.equipmentClass || "").trim() || null,
                equipmentMake: (active.equipmentMake || "").trim() || null,
                equipmentModel: (active.equipmentModel || "").trim() || null,
                rate: parseRate(active.rate),
                delayFactor: parseRate(active.delayFactor),
                otFactor: parseRate(active.otFactor),
              }),
        })
      );

      if (newEquipment && num === equipmentNumbers[0]) {
        dispatch({
          type: ADR_EDIT.EQUIPMENTSET.UPDATE,
          tabId,
          rowKey,
          fields: {
            contractorId: newEquipment.contractorId,
            equipmentId: newEquipment.equipmentId,
          },
        });
        markAdrDirty();
      }
    }

    toggleAddEquipmentModal();
  };

  // ── Render helpers ───────────────────────────────────────────────────────

  renderSearchBar() {
    const { search, formData } = this.state;
    return (
      <div className="form-row form-group mb-2">
        <label className="col-sm-3 col-form-label">Search ERRB:</label>
        <div className="col-sm-9" style={{ position: "relative" }}>
          <input
            type="text"
            className="form-control"
            value={search.query}
            onChange={this.handleSearchChange}
            placeholder="Class / Make / Code  e.g. ROLLER / BOMAG / BW211"
            disabled={!formData.contractorId}
            autoComplete="off"
          />
          {search.loading && (
            <small className="text-muted ml-1">Searching...</small>
          )}
          {search.results.length > 0 && (
            <div
              className="list-group shadow-sm"
              style={{ position: "absolute", top: "100%", left: 0, right: 0, zIndex: 9999, maxHeight: 220, overflowY: "auto" }}
            >
              {search.results.map((r, i) => (
                <button
                  key={i}
                  type="button"
                  className="list-group-item list-group-item-action py-1 px-2 text-left"
                  style={{ fontSize: "0.78rem" }}
                  onClick={() => this.handleSearchSelect(r)}
                >
                  {r.equipmentClassDescription}{" "}
                  <b>{r.equipmentClass}</b>
                  {" / "}
                  {r.equipmentMakeDescription}{" "}
                  <b>{r.equipmentMake}</b>
                  {" / "}
                  {r.equipmentModelDescription}{" "}
                  <b>{r.equipmentModel}</b>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  renderCatalogDropdowns() {
    const { formData, catalog, reconciling } = this.state;
    const active = this.getActiveSelection();
    const busy = catalog.loadingClasses || catalog.loadingMakes || catalog.loadingModels || reconciling;
    const noModelsAvailable = !busy && !!active.equipmentMake && catalog.models.length === 0 && !active.selectedModelOption;

    const classOptions = catalog.classes.map((c) => ({
      value: c.equipmentClass,
      label: `[ ${c.equipmentClass} ] ${c.equipmentClassDescription}`,
      description: c.equipmentClassDescription,
    }));
    const makeOptions = catalog.makes.map((m) => ({
      value: m.equipmentMake,
      label: `[ ${m.equipmentMake} ] ${m.equipmentMakeDescription}`,
      description: m.equipmentMakeDescription,
    }));
    const modelOptions = catalog.models.map((m) => ({
      value: m.equipmentModel,
      label: `[ ${m.equipmentModel} ] ${m.equipmentModelDescription}`,
      description: m.equipmentModelDescription,
    }));

    return (
      <React.Fragment>
        <div className="form-row form-group mb-2">
          <label className="col-sm-3 col-form-label"><b>Class:</b>*</label>
          <div className="col-sm-9">
            <Select
              value={active.selectedClassOption}
              options={classOptions}
              onChange={this.handleChange_class}
              isDisabled={!formData.contractorId || busy}
              isLoading={busy && catalog.classes.length === 0}
              placeholder="(Select class)"
              isClearable
              styles={{ menu: (p) => ({ ...p, zIndex: 9999 }) }}
            />
          </div>
        </div>
        <div className="form-row form-group mb-2">
          <label className="col-sm-3 col-form-label"><b>Make:</b>*</label>
          <div className="col-sm-9">
            <Select
              value={active.selectedMakeOption}
              options={makeOptions}
              onChange={this.handleChange_make}
              isDisabled={!active.equipmentClass || busy}
              isLoading={busy && !!active.equipmentClass && catalog.makes.length === 0}
              placeholder="(Select make)"
              isClearable
              styles={{ menu: (p) => ({ ...p, zIndex: 9999 }) }}
            />
          </div>
        </div>
        <div className="form-row form-group mb-2">
          <label className="col-sm-3 col-form-label"><b>Model Code:</b>{!noModelsAvailable && "*"}</label>
          <div className="col-sm-9">
            {noModelsAvailable ? (
              <p className="form-control-plaintext text-muted mb-0">
                <small>(No code for this make)</small>
              </p>
            ) : (
              <Select
                value={active.selectedModelOption}
                options={modelOptions}
                onChange={this.handleChange_model}
                isDisabled={!active.equipmentMake || busy}
                isLoading={busy && !!active.equipmentMake && catalog.models.length === 0}
                placeholder="(Select code)"
                isClearable
                styles={{ menu: (p) => ({ ...p, zIndex: 9999 }) }}
              />
            )}
          </div>
        </div>
        {reconciling && (
          <small className="text-muted d-block mb-2">
            <FontAwesomeIcon icon="spinner" spin className="fa-fw mr-1" />
            Checking catalog for the selected type/year...
          </small>
        )}
      </React.Fragment>
    );
  }

  renderManualFields() {
    const { formData } = this.state;
    const active = this.getActiveSelection();
    return (
      <React.Fragment>
        <div className="form-row form-group mb-2">
          <label className="col-sm-3 col-form-label">Class:</label>
          <div className="col-sm-9">
            <input
              type="text"
              name="equipmentClass"
              className="form-control"
              value={active.equipmentClass}
              onChange={this.handleChangeManualField}
              placeholder="(optional)"
              disabled={!formData.contractorId}
            />
          </div>
        </div>
        <div className="form-row form-group mb-2">
          <label className="col-sm-3 col-form-label">Make:</label>
          <div className="col-sm-9">
            <input
              type="text"
              name="equipmentMake"
              className="form-control"
              value={active.equipmentMake}
              onChange={this.handleChangeManualField}
              placeholder="(optional)"
              disabled={!formData.contractorId}
            />
          </div>
        </div>
        <div className="form-row form-group mb-2">
          <label className="col-sm-3 col-form-label">Model Code:</label>
          <div className="col-sm-9">
            <input
              type="text"
              name="equipmentModel"
              className="form-control"
              value={active.equipmentModel}
              onChange={this.handleChangeManualField}
              placeholder="(optional)"
              disabled={!formData.contractorId}
            />
          </div>
        </div>
      </React.Fragment>
    );
  }

  renderManualRateFields() {
    const { formData } = this.state;
    const active = this.getActiveSelection();
    return (
      <div className="form-row form-group mb-2">
        <label className="col-sm-3 col-form-label">Rate:</label>
        <div className="col-sm-9 form-row no-gutters">
          <div className="col-4 pr-1">
            <div className="input-group input-group-sm">
              <div className="input-group-prepend">
                <span className="input-group-text">$</span>
              </div>
              <input
                type="number" step="0.01" name="rate" className="form-control"
                value={active.rate} onChange={this.handleChangeManualField}
                placeholder="Rate/hr" disabled={!formData.contractorId}
              />
            </div>
          </div>
          <div className="col-4 px-1">
            <input
              type="number" step="0.01" name="otFactor" className="form-control form-control-sm"
              value={active.otFactor} onChange={this.handleChangeManualField}
              placeholder="OT Factor" disabled={!formData.contractorId}
            />
          </div>
          <div className="col-4 pl-1">
            <input
              type="number" step="0.01" name="delayFactor" className="form-control form-control-sm"
              value={active.delayFactor} onChange={this.handleChangeManualField}
              placeholder="Delay Factor" disabled={!formData.contractorId}
            />
          </div>
        </div>
      </div>
    );
  }

  render() {
    const {
      formData, mode, showCatalogBrowser, selectedCatalogYear, catalogYearOptions,
      catalog, catalogRate, reconciling, search,
    } = this.state;
    const { showAddEquipmentModal, toggleAddEquipmentModal, contractorOptions } = this.props;
    const active = this.getActiveSelection();
    const isCatalog = mode !== MODE.MANUAL;
    const busy = catalog.loadingClasses || catalog.loadingMakes || catalog.loadingModels || reconciling;
    const duplicateNumbers = this.getDuplicateNumbers();

    const closeBtn = (
      <button className="close" onClick={toggleAddEquipmentModal}>
        <FontAwesomeIcon icon="times" className="fa-fw text-white" />
      </button>
    );

    const modeLabel =
      mode === MODE.MANUAL ? "Manual Entry"
      : mode === MODE.STANDARD ? "Standard"
      : "Miscellaneous";

    return (
      <React.Fragment>
      <Modal
        isOpen={showAddEquipmentModal}
        toggle={toggleAddEquipmentModal}
        fade={false}
        size="lg"
      >
        <ModalHeader
          toggle={toggleAddEquipmentModal}
          className="bg-info text-white"
          close={closeBtn}
        >
          <FontAwesomeIcon icon="plus" className="fa-fw mr-2" />
          Add Equipment — {modeLabel}
        </ModalHeader>
        <ModalBody>
          <form id="addEquipForm" onSubmit={this.handleSubmit} autoComplete="off">
            {/* Contractor */}
            <div className="card mx-2 mb-2 bg-lightgray">
              <div className="card-body">
                <div className="form-row form-group mb-0">
                  <label className="col-sm-3 col-form-label"><b>Contractor:</b>*</label>
                  <div className="col-sm-6 my-auto">
                    <Select
                      name="contractorId"
                      id="contractorId"
                      value={formData.selectedContractor}
                      options={contractorOptions || []}
                      onChange={this.handleChange_contractor}
                      placeholder="(Select a Contractor)"
                      styles={{ menu: (p) => ({ ...p, zIndex: 9999 }) }}
                    />
                  </div>
                </div>
              </div>
            </div>

            {!!formData.contractorId && (
            <React.Fragment>
            {/* Entry mode toggle */}
            <div className="card mx-2 mt-2 bg-lightgray">
              <div className="card-body py-2">
                {/* Catalog Year (catalog modes only) */}
                {isCatalog && (
                  <div className="form-row align-items-center mt-1">
                    <label className="col-sm-3 col-form-label"><b>Rate Year:</b></label>
                    <div className="col-sm-7 my-auto d-flex align-items-center">
                      <div style={{ flex: "0 0 110px" }}>
                        <Select
                          value={selectedCatalogYear}
                          options={catalogYearOptions}
                          onChange={this.handleCatalogYearChange}
                          styles={{ menu: (p) => ({ ...p, zIndex: 9999 }) }}
                          isSearchable={false}
                        />
                      </div>
                      <button
                        type="button"
                        className="btn btn-outline-secondary ml-2 bg-info text-white flex-fill"
                        onClick={() => this.setState({ showCatalogBrowser: true })}
                        disabled={!formData.contractorId}
                      >
                        <FontAwesomeIcon icon="book" className="fa-fw mr-1" />
                        Browse Rental Rate Book
                      </button>
                    </div>
                  </div>
                )}
                <div className="form-row mt-2">
                  <label className="col-sm-3 col-form-label"><b>Rate Type:</b></label>
                  <div className="col-sm-7 my-auto d-flex align-items-center">
                    <div className="btn-group btn-group-sm d-flex w-100" role="group">
                      {[
                        { key: MODE.STANDARD, label: "Standard" },
                        { key: MODE.MISC, label: "Miscellaneous" },
                        { key: MODE.MANUAL, label: "Manual Entry" },
                      ].map(({ key, label }) => (
                        <button
                          key={key}
                          type="button"
                          className={`btn ${mode === key ? "btn-primary" : "btn-outline-secondary"}`}
                          style={{ flex: "1 1 0" }}
                          onClick={() => this.handleModeChange(key)}
                        >
                          {label}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            {/* Common: Number */}
            <div className="card m-2">
              <div className="card-body">
                <div className="form-row form-group mb-3">
                  <label htmlFor="equipmentNumber" className="col-sm-3 col-form-label">
                    <b>Number:</b>*
                  </label>
                  <div className="col-sm-9">
                    <input
                      type="text"
                      name="equipmentNumber"
                      id="equipmentNumber"
                      className="form-control"
                      maxLength="20"
                      value={formData.equipmentNumber}
                      onChange={this.handleChange}
                      disabled={!formData.contractorId}
                    />
                    <small className="text-muted">If more than one, separate by comma.</small>
                    {duplicateNumbers.length > 0 && (
                      <div className="alert alert-warning py-1 px-2 mb-0 mt-1">
                        <FontAwesomeIcon icon="exclamation-triangle" className="fa-fw mr-1" />
                        <small>
                          Equipment number{duplicateNumbers.length > 1 ? "s" : ""}{" "}
                          <b>{duplicateNumbers.join(", ")}</b>{" "}
                          already exist{duplicateNumbers.length === 1 ? "s" : ""} for this contractor.
                        </small>
                      </div>
                    )}
                  </div>
                </div>

                {/* ── Search bar (catalog modes) ── */}
                {isCatalog && this.renderSearchBar()}

                {/* ── Catalog mode: cascading dropdowns, must match the catalog ── */}
                {isCatalog && this.renderCatalogDropdowns()}

                {/* ── Manual mode: free-form, unvalidated Class/Make/Code + static rate ── */}
                {mode === MODE.MANUAL && this.renderManualFields()}

                <div className="form-row form-group mb-2">
                  {mode === MODE.MANUAL && (
                    <label className="col-sm-3 col-form-label">
                      <b>Description*:</b>
                    </label>
                  )}
                  {mode !== MODE.MANUAL && (
                    <label className="col-sm-3 col-form-label">
                      Description:
                    </label>
                  )}
                  <div className="col-sm-9">
                    <input
                      type="text"
                      name="description"
                      id="description"
                      className="form-control"
                      value={formData.description}
                      onChange={this.handleChange}
                      placeholder="Enter Description (e.g. Ford F150)"
                      disabled={!formData.contractorId}
                    />
                  </div>
                </div>

                {isCatalog && catalogRate && (
                  <div className="alert alert-info py-2 mb-2">
                    <small>
                      <b>Rate ({selectedCatalogYear?.label || ""}):</b> ${catalogRate.rate}/hr &nbsp;|&nbsp;
                      OT Factor: {catalogRate.otFactor} &nbsp;|&nbsp;
                      Delay Factor: {catalogRate.delayFactor}
                    </small>
                  </div>
                )}

                {mode === MODE.MANUAL && this.renderManualRateFields()}

                {/* IsRental */}
                <div className="form-row mb-0">
                  <div className="col-sm-3"></div>
                  <div className="col-sm-9">
                    <div className="custom-control-inline custom-control custom-checkbox">
                      <input
                        type="checkbox"
                        name="isRental"
                        id="isRental"
                        className="custom-control-input my-auto ml-2"
                        disabled={!formData.contractorId}
                        checked={formData.isRental}
                        onChange={this.handleChange_check}
                      />
                      <label htmlFor="isRental" className="custom-control-label my-auto">
                        This is rental equipment
                      </label>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            </React.Fragment>
            )}
            <div className="ml-2">
              <small>* indicates required fields</small>
            </div>
          </form>
        </ModalBody>
        <ModalFooter className="bg-lightgray">
          <div className="form-row form-group mb-0 w-100 justify-content-between align-items-center">
            {isCatalog && !catalogRate && (
              <span className="my-auto">
                <small className="text-muted">
                  Rate resolved at reporting time from the Caltrans rental rate catalog.
                </small>
              </span>
            )}
            {(!isCatalog || catalogRate) && <span />}
            <div>
              <input
                className="btn btn-primary"
                name="addEquip"
                type="submit"
                form="addEquipForm"
                onClick={this.handleSubmit}
                value="Add"
                disabled={this.formIsIncomplete()}
              />
              <button className="btn btn-secondary ml-2" onClick={toggleAddEquipmentModal}>
                Cancel
              </button>
            </div>
          </div>
        </ModalFooter>
      </Modal>
      <EquipmentCatalogBrowserModal
        isOpen={showCatalogBrowser}
        equipmentType={this.equipmentTypeParam}
        reportDate={this.reportDateStr}
        catalogYearLabel={selectedCatalogYear ? selectedCatalogYear.label : ""}
        onClose={() => this.setState({ showCatalogBrowser: false })}
        onSelect={this.handleCatalogBrowserSelect}
      />
      </React.Fragment>
    );
  }
}

const mapStateToProps = (state, ownProps) => ({
  user: state.authentication.user,
  reportDate:
    ownProps.tabId && state.adr.openAdrs[ownProps.tabId]
      ? state.adr.openAdrs[ownProps.tabId].reportStartDate || null
      : null,
  projEquipList: state.equipment.projEquipLists[ownProps.projectId] || [],
});

export default connect(mapStateToProps)(AddEquipmentModalV3);
